import { Link } from "react-router-dom";
import { ChevronLeft, ShieldCheck, FileText, ExternalLink, MapPin } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { StoreFooter } from "@/components/StoreFooter";

function StorefrontHeader() {
  return (
    <>
      <div className="gov-strip">
        <span className="tri"><span className="b saffron" /><span className="b white" /><span className="b green" /></span>
        <Link to="/about">About us</Link>
        <Link to="/contact">Contact us</Link>
        <Link to="/gi-information">GI information</Link>
      </div>
      <header className="store-header">
        <Link to="/" className="store-brand" data-testid="brand-home">
          <LogoMark small />
          <span>GI Bharat<small>By True Vision Welfare Society · Authorised-user services</small></span>
        </Link>
        <div className="header-actions">
          <Link to="/authorised-users" data-testid="header-authorised-link">Authorised users</Link>
          <Link to="/login" data-testid="header-login">Login</Link>
        </div>
      </header>
    </>
  );
}

const OFFICIAL_LINKS = [
  { label: "GI Registry (IP India)", url: "https://ipindia.gov.in/registry", hint: "Official registry" },
  { label: "Application forms", url: "https://ipindia.gov.in/geographical-indications-before-you-apply-forms", hint: "Before-you-apply" },
  { label: "Public GI search", url: "https://search.ipindia.gov.in/GIRPublicSearch/", hint: "Registered products" },
  { label: "Download Journal (GIR)", url: "https://search.ipindia.gov.in/IPOJournal/Journal/GIR", hint: "Official journal" },
];

const CLASSES = [
  ["1", "Chemicals used in industry, science, agriculture, horticulture and forestry; fertilizers and industrial adhesives"],
  ["2", "Paints, varnishes, lacquers, preservatives, colorants and natural resins"],
  ["3", "Cleaning preparations, soaps, perfumes, essential oils, cosmetics and dental preparations"],
  ["4", "Industrial oils, greases, lubricants, fuels, illuminants and candles"],
  ["5", "Pharmaceutical, veterinary and sanitary preparations; disinfectants and herbicides"],
  ["6", "Common metals, metal building materials, pipes, cables, safes and ores"],
  ["7", "Machines, machine tools, engines, motors, agricultural machinery and incubators"],
  ["8", "Hand-operated tools, implements, cutlery and razors"],
  ["9", "Scientific, electrical, optical, photographic, measuring and computer equipment"],
  ["10", "Medical, surgical, dental and veterinary instruments; prosthetic articles"],
  ["11", "Lighting, heating, cooking, refrigerating, drying, ventilating and sanitary apparatus"],
  ["12", "Vehicles and means of transportation by land, air or water"],
  ["13", "Firearms, ammunition, explosives and fireworks"],
  ["14", "Precious metals, jewellery, precious stones and watches"],
  ["15", "Musical instruments"],
  ["16", "Paper, cardboard, printed materials, stationery and teaching materials"],
  ["17", "Rubber, plastics, asbestos, mica and insulating materials"],
  ["18", "Leather, animal skins, luggage, umbrellas and saddlery"],
  ["19", "Non-metallic building materials, asphalt, bitumen and monuments"],
  ["20", "Furniture, mirrors, picture frames, wood, cork and cane"],
  ["21", "Household and kitchen utensils, containers, glassware, porcelain and brushes"],
  ["22", "Ropes, strings, nets, tents, tarpaulins, sacks and raw textile fibres"],
  ["23", "Yarns and threads for textile use"],
  ["24", "Textiles, textile goods, bed covers and table covers"],
  ["25", "Clothing, footwear and headgear"],
  ["26", "Lace, embroidery, ribbons, buttons, hooks, needles and artificial flowers"],
  ["27", "Carpets, rugs, mats, floor coverings and non-textile wall hangings"],
  ["28", "Games, toys, sporting goods and Christmas decorations"],
  ["29", "Meat, fish, poultry, preserved fruits, vegetables, dairy, eggs and edible oils"],
  ["30", "Coffee, tea, cocoa, rice, flour, cereals, bread, spices, salt and sauces"],
  ["31", "Agricultural, horticultural and forestry products; seeds, plants and animal feed"],
  ["32", "Beer, mineral and aerated waters, non-alcoholic beverages, fruit drinks and syrups"],
  ["33", "Alcoholic beverages other than beer"],
  ["34", "Tobacco, smokers' articles and matches"],
];

const TOC = [
  ["understanding", "Understanding Geographical Indications"],
  ["about", "About Geographical Indications"],
  ["mission", "Our Vision & Mission"],
  ["what", "What is a Geographical Indication?"],
  ["why", "Why register a Geographical Indication?"],
  ["who-apply", "Who can apply?"],
  ["proprietor", "Registered proprietor"],
  ["auth-user", "Authorised user"],
  ["producer", "Who is a producer?"],
  ["not-registrable", "What cannot be registered?"],
  ["validity", "Validity and renewal"],
  ["filing", "Application filing requirements"],
  ["process", "GI registration process"],
  ["classification", "Classification of goods"],
  ["identity", "Protecting the identity of a place"],
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="gi-section" data-testid={`gi-section-${id}`}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export default function GIInformation() {
  return (
    <main className="storefront gi-info" data-testid="gi-information-page">
      <StorefrontHeader />

      {/* Hero */}
      <section className="gi-hero">
        <div className="gi-hero-inner">
          <div>
            <span className="gi-eyebrow">GOVERNMENT OF INDIA · INTELLECTUAL PROPERTY</span>
            <h1>Geographical Indications (GI)</h1>
            <p>
              Every place has something that makes it special. A Geographical Indication is an intellectual property
              right that ties a product's identity — its quality, reputation and characteristics — to the region where
              it originates. India protects such products under the <b>Geographical Indications of Goods (Registration
              and Protection) Act, 1999</b>, in force since 15 September 2003.
            </p>
            <div className="gi-hero-cta">
              <Link to="/login" className="primary-btn" data-testid="gi-hero-cta-register"><FileText size={15} /> Start authorised-user registration</Link>
              <a href="https://ipindia.gov.in/registry" target="_blank" rel="noopener noreferrer" className="secondary-btn" data-testid="gi-hero-cta-registry">Visit IP India registry <ExternalLink size={13} /></a>
            </div>
          </div>
          <aside className="gi-hero-card">
            <p className="gi-hero-card-label"><ShieldCheck size={14} /> AT A GLANCE</p>
            <ul>
              <li><b>Governing Act</b><span>GI Act, 1999</span></li>
              <li><b>In force since</b><span>15 September 2003</span></li>
              <li><b>Registry office</b><span>Chennai, India</span></li>
              <li><b>Protection term</b><span>10 years · renewable</span></li>
              <li><b>Covers</b><span>Agricultural · Natural · Handicraft · Manufactured</span></li>
            </ul>
          </aside>
        </div>
      </section>

      {/* Table of contents */}
      <section className="gi-toc" data-testid="gi-toc">
        <div className="gi-toc-inner">
          <span className="gi-eyebrow small">ON THIS PAGE</span>
          <ol>
            {TOC.map(([id, t]) => <li key={id}><a href={`#${id}`}>{t}</a></li>)}
          </ol>
        </div>
      </section>

      <div className="gi-body">
        <Section id="understanding" title="Understanding Geographical Indications">
          <p>Across history, certain products have become famous because of the region where they were grown, made, crafted, or traditionally developed. Indian spices attracted explorers from Europe; Arabian horses became renowned for their breeding qualities; and products such as Chinese silk, Dhaka muslin and Venetian glass earned worldwide recognition.</p>
          <p>These products did not become famous overnight. Their reputation was built over generations through a unique combination of local resources, climate, traditional knowledge, craftsmanship and human skill. Over time, a strong connection developed between the product and the geographical region from which it originated. This connection is what forms the foundation of a Geographical Indication (GI).</p>
          <p>India enacted the Geographical Indications of Goods (Registration and Protection) Act, 1999 to establish a legal framework for registering and protecting such indications. The Act came into force on <b>15 September 2003</b> and is administered through the Geographical Indications Registry under the Controller General of Patents, Designs and Trade Marks.</p>
        </Section>

        <Section id="about" title="About Geographical Indications">
          <p>A Geographical Indication is an intellectual property right that identifies goods as originating from a particular country, region or locality where a specific quality, reputation, characteristic or other attribute of the goods is essentially connected with that geographical origin. In simple terms, a GI tells consumers: <i>“This product comes from this particular place, and its distinctive identity is connected to that place.”</i></p>
          <p>A GI may relate to agricultural products, natural products, handicrafts or manufactured goods. The uniqueness of such goods may arise from the region's climate, soil, natural resources, traditional techniques, indigenous knowledge, craftsmanship, or a combination of these factors.</p>
          <p>Geographical Indications form an important part of the international intellectual property system. They are recognised under the <b>Paris Convention for the Protection of Industrial Property</b> and receive specific protection under <b>Articles 22–24 of the TRIPS Agreement</b>. As a member of the WTO, India introduced its dedicated GI legislation to protect India's geographically distinctive products.</p>
        </Section>

        <Section id="mission" title="Our Vision & Mission">
          <p>The Geographical Indications Registry works towards strengthening the protection and promotion of India's geographically distinctive products. The mission is to:</p>
          <ul>
            <li>Protect the identity and reputation of registered Geographical Indications.</li>
            <li>Prevent unauthorised use and misleading representation of GI names.</li>
            <li>Preserve traditional knowledge, craftsmanship and production practices.</li>
            <li>Support producers associated with recognised geographical regions.</li>
            <li>Encourage quality, authenticity and consistency in GI products.</li>
            <li>Contribute to the economic development of producer communities.</li>
            <li>Strengthen recognition of Indian GI products in domestic and international markets.</li>
          </ul>
        </Section>

        <Section id="what" title="What is a Geographical Indication?">
          <p>Under Indian law, a Geographical Indication in relation to goods generally refers to an indication that:</p>
          <ul>
            <li>Identifies goods with a particular geographical territory.</li>
            <li>Originates from a clearly defined region, locality or country.</li>
            <li>Relates to agricultural, natural or manufactured products.</li>
            <li>For manufactured goods, is produced, processed or prepared in the specified territory.</li>
            <li>Possesses a distinctive quality, reputation or characteristic associated with its geographical origin.</li>
          </ul>
          <div className="gi-callout"><MapPin size={16} /> <b>Place + Product + Distinctive Characteristics = Geographical Indication</b></div>
        </Section>

        <Section id="why" title="Why register a Geographical Indication?">
          <ol className="gi-benefits">
            <li><b>Legal protection</b><span>Statutory protection to the GI under Indian law.</span></li>
            <li><b>Protection against misuse</b><span>Prevents unauthorised parties from using the registered GI for products that don't originate from the specified region.</span></li>
            <li><b>Preserving India's GI heritage</b><span>Safeguards traditional products and geographically distinctive goods.</span></li>
            <li><b>Support for exports</b><span>Strengthens credibility and market identity of Indian GI products internationally.</span></li>
            <li><b>Economic development</b><span>Improves market opportunities and contributes to the prosperity of producer communities.</span></li>
            <li><b>Preservation of traditional knowledge</b><span>Helps maintain traditional production methods, craftsmanship and cultural heritage.</span></li>
          </ol>
        </Section>

        <Section id="who-apply" title="Who can apply for GI registration?">
          <p>An application may be submitted by:</p>
          <ul>
            <li>An association of persons</li>
            <li>An association of producers</li>
            <li>An organisation</li>
            <li>An authority established by or under law</li>
          </ul>
          <p>The applicant must genuinely represent the interests of the producers concerned. The application must be submitted in the prescribed form and in writing, include the required supporting documents, and be accompanied by the applicable fee.</p>
        </Section>

        <Section id="proprietor" title="Who is the registered proprietor?">
          <p>The registered proprietor is the association of persons, producers, organisation or authority whose name is entered in the Register of Geographical Indications as the proprietor of the registered GI. The proprietor represents the collective interests associated with the GI rather than owning the geographical indication in the same manner as a conventional private trademark owner.</p>
        </Section>

        <Section id="auth-user" title="Who is an Authorised User?">
          <p>An authorised user is a producer who has the right to use a registered Geographical Indication. A producer seeking authorised-user status must:</p>
          <ul>
            <li>Be a producer of goods covered by the registered GI</li>
            <li>Apply in the prescribed manner</li>
            <li>Submit the required documents</li>
            <li>Pay the applicable prescribed fee</li>
          </ul>
          <p>Authorised-user registration helps distinguish legitimate producers from unauthorised users of the GI.</p>
        </Section>

        <Section id="producer" title="Who is considered a producer?">
          <div className="gi-cards">
            <div className="gi-card"><h4>Agricultural products</h4><p>Persons involved in growing/producing, processing, trading or otherwise dealing with agricultural goods.</p></div>
            <div className="gi-card"><h4>Natural products</h4><p>Persons involved in exploiting/collecting, trading or otherwise dealing with natural goods.</p></div>
            <div className="gi-card"><h4>Handicraft & industrial</h4><p>Persons involved in making, manufacturing, processing, trading or otherwise dealing with the relevant goods.</p></div>
          </div>
        </Section>

        <Section id="not-registrable" title="What cannot be registered as a GI?">
          <p>A GI generally cannot be registered where:</p>
          <ul>
            <li>Its use is likely to deceive or confuse consumers.</li>
            <li>Its registration or use would be contrary to applicable law.</li>
            <li>It contains scandalous or obscene matter.</li>
            <li>It contains matter likely to hurt religious sensibilities of a section of Indian citizens.</li>
            <li>It would otherwise be denied protection by a court.</li>
            <li>It has become a generic name or has fallen into disuse in its country of origin.</li>
            <li>Although technically accurate, it gives consumers a false impression that the goods originate from another territory.</li>
          </ul>
        </Section>

        <Section id="validity" title="Validity and renewal">
          <p>A registered GI is protected for <b>10 years from the date of application</b>. It may be renewed for successive 10-year periods on filing the required documents and paying the prescribed fee.</p>
          <div className="gi-callout warn"><b>Important:</b> Failure to renew within the applicable period may result in removal of the GI from the Register.</div>
        </Section>

        <Section id="filing" title="Application filing requirements">
          <ol className="gi-steps">
            <li><b>Application form & signature</b><span>Filed in the prescribed GI form with the applicable fee, signed by the applicant or authorised agent, in the required number of copies, along with the Statement of Case.</span></li>
            <li><b>Payment of fees</b><span>May be paid by cash (where applicable), money order, bank draft or cheque meeting the Registry's prescribed requirements.</span></li>
            <li><b>Document format</b><span>Prepared in Hindi or English, clearly typed/printed on good-quality paper, with prescribed margins and adequate line spacing. A4 size with ~12-point font is generally recommended.</span></li>
            <li><b>Who should sign?</b><span>Association: authorised signatory. Company/Organisation: Chief Executive, MD, Secretary or authorised principal officer. Partnership: at least one partner. Name and capacity must be clearly mentioned below the signature.</span></li>
            <li><b>Principal place of business</b><span>Applicant's principal place of business in India (and, for corporates, director details) is required. Foreign applicants must provide an address for service in India.</span></li>
            <li><b>Convention applications</b><span>Where priority is claimed, include certification from the convention country, details of the foreign application, filing date and application number.</span></li>
            <li><b>Statement of user</b><span>Application must contain the required statement of use, supported by an affidavit where prescribed.</span></li>
            <li><b>What the application should contain</b><span>Origin/distinctiveness, product classification, geographical map, representation of the GI, producer details, product standards, quality-control mechanism, geographical/environmental factors, inspection structure and homonymous-indication details.</span></li>
          </ol>
        </Section>

        <Section id="process" title="GI registration process">
          <div className="gi-timeline">
            {[
              ["Step 1", "Filing", "Submit application with Statement of Case, maps, producer details, evidence and fees."],
              ["Step 2", "Preliminary scrutiny", "Initial scrutiny for deficiencies; correct any within the prescribed period."],
              ["Step 3", "Examination", "Application and Statement of Case examined; experts may be consulted; Examination Report may be issued."],
              ["Step 4", "Show-cause proceedings", "Applicant may respond, submit evidence and request a hearing where objections are raised."],
              ["Step 5", "Publication in GI Journal", "Accepted application is published for public opposition."],
              ["Step 6", "Opposition", "Notice of opposition, counter-statement, evidence and hearing (if any); Registrar's decision follows."],
              ["Step 7", "Registration", "Registrar enters the GI in the Register and issues a certificate to the proprietor."],
              ["Step 8", "Renewal", "Valid for 10 years; renewable for successive 10-year periods on payment of prescribed fee."],
              ["Step 9", "Additional protection", "Certain notified goods may receive additional protection on filing the prescribed application."],
              ["Step 10", "Appeal", "A person aggrieved may appeal before the appropriate High Court, subject to applicable law."],
            ].map(([s, t, d]) => (
              <div className="gi-tl-item" key={s}>
                <span className="gi-tl-step">{s}</span>
                <div>
                  <h4>{t}</h4>
                  <p>{d}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="gi-registry-card">
            <h4>Geographical Indications Registry</h4>
            <p>Intellectual Property Office Building, Industrial Estate, G.S.T. Road, Guindy, Chennai – 600 032, India</p>
            <p><b>Phone:</b> 044-22502030 · <b>Email:</b> gir-ipo[at]nic[dot]in</p>
            <a href="https://ipindia.gov.in/registry" target="_blank" rel="noopener noreferrer">Official website <ExternalLink size={12} /></a>
          </div>
        </Section>

        <Section id="classification" title="Classification of goods">
          <p>For GI registration purposes, goods are classified according to the relevant international classification system. The principal classes include:</p>
          <div className="gi-class-table">
            <div className="gi-class-head"><span>Class</span><span>Broad category</span></div>
            {CLASSES.map(([n, d]) => (
              <div className="gi-class-row" key={n}><span>{n}</span><span>{d}</span></div>
            ))}
          </div>
        </Section>

        <Section id="identity" title="GI: Protecting the identity of a place">
          <p>A Geographical Indication is more than a name on a product. It represents the relationship between a product, its people and its place of origin. The strength of a GI lies in the story behind the product — the land, climate, traditional knowledge, local skills, cultural practices and generations of producers who have contributed to its reputation.</p>
          <p>Effective GI protection helps achieve three important objectives:</p>
          <ul>
            <li>Protect the <b>place</b>.</li>
            <li>Protect the <b>product</b>.</li>
            <li>Protect the <b>people</b> who preserve its legacy.</li>
          </ul>
        </Section>

        <section className="gi-official-links" data-testid="gi-official-links">
          <h3>Official references</h3>
          <div className="gi-official-grid">
            {OFFICIAL_LINKS.map((l, i) => (
              <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`gi-ref-${i}`}>
                <span>{l.hint}</span>
                <b>{l.label}</b>
                <em>Open <ExternalLink size={11} /></em>
              </a>
            ))}
          </div>
        </section>

        <Link to="/" className="pd-back" style={{ marginTop: 16, display: "inline-flex" }}><ChevronLeft size={14} /> Back to home</Link>
      </div>

      <StoreFooter />
    </main>
  );
}
